import { useState, useEffect, useMemo } from 'react';
import { useParams, useSearchParams, Link } from 'react-router-dom';
import SEOHead from '@/components/feature/SEOHead';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import ComicCard from '@/components/feature/ComicCard';
import ScrollToTop from '@/components/base/ScrollToTop';
import { comics, categories } from '@/mocks/comics';

const siteUrl = 'https://www.hanman.com';

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();
  const [searchParams] = useSearchParams();
  const searchQuery = searchParams.get('search') || '';
  const [activeSlug, setActiveSlug] = useState(slug || 'all');

  useEffect(() => {
    if (slug) {
      setActiveSlug(slug);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  }, [slug]);

  const filteredComics = useMemo(() => {
    let result = [...comics];

    if (activeSlug && activeSlug !== 'all') {
      result = result.filter((c) => c.categorySlug === activeSlug);
    }

    if (searchQuery) {
      const q = searchQuery.toLowerCase();
      result = result.filter(
        (c) =>
          c.title.toLowerCase().includes(q) ||
          c.author.toLowerCase().includes(q) ||
          c.tags.some((t) => t.toLowerCase().includes(q))
      );
    }

    return result;
  }, [activeSlug, searchQuery]);

  const activeCategory = categories.find((c) => c.slug === activeSlug);
  const pageTitle = searchQuery
    ? `搜索"${searchQuery}" - 韩漫网`
    : activeCategory && activeCategory.slug !== 'all'
    ? `${activeCategory.name}韩国漫画 - 韩漫网`
    : '全部分类 - 韩漫网-免费韩国漫画大全';
  const pageDesc = activeCategory && activeCategory.slug !== 'all'
    ? `韩漫网为您提供精选${activeCategory.name}类韩国漫画免费在线阅读，涵盖多种风格与题材，资源齐全更新及时。`
    : '韩漫网为您提供海量正版韩国漫画免费在线阅读，按分类浏览各类漫画作品。';
  const pageKeywords = `韩漫网,韩国漫画,${activeCategory?.name || '全部分类'},免费漫画,在线阅读`;

  const breadcrumbItems = [
    { label: '分类', href: '/category/all' },
    ...(activeCategory && activeCategory.slug !== 'all' ? [{ label: activeCategory.name }] : []),
    ...(searchQuery ? [{ label: `搜索: ${searchQuery}` }] : []),
  ];

  const schemaData = {
    '@context': 'https://schema.org',
    '@type': 'CollectionPage',
    name: pageTitle,
    description: pageDesc,
    url: `${siteUrl}/category/${activeSlug}`,
    isPartOf: {
      '@type': 'WebSite',
      name: '韩漫网',
      url: siteUrl,
    },
  };

  return (
    <>
      <SEOHead
        title={pageTitle}
        description={pageDesc}
        keywords={pageKeywords}
        canonicalUrl={`${siteUrl}/category/${activeSlug}`}
        schemaData={schemaData}
      />
      <Navbar />
      <main className="pt-16">
        <div className="max-w-[1400px] mx-auto px-4 md:px-6">
          <Breadcrumb items={breadcrumbItems} />

          <div className="mb-8">
            <h1 className="text-2xl md:text-3xl font-bold text-foreground-950" style={{ fontFamily: 'var(--font-heading)' }}>
              {searchQuery ? `搜索: "${searchQuery}"` : activeCategory?.name || '全部分类'}
            </h1>
            {searchQuery && (
              <p className="text-sm text-foreground-500 mt-2">
                找到 {filteredComics.length} 个相关结果
              </p>
            )}
          </div>

          <div className="flex gap-2 pb-6 overflow-x-auto flex-wrap">
            {categories.map((cat) => (
              <Link
                key={cat.slug}
                to={`/category/${cat.slug}`}
                className={`inline-flex items-center gap-1.5 px-4 py-2 rounded-full text-sm font-medium transition-all cursor-pointer whitespace-nowrap ${
                  activeSlug === cat.slug
                    ? 'bg-primary-500 text-background-50'
                    : 'bg-background-100 text-foreground-600 hover:bg-background-200'
                }`}
              >
                <i className={`${cat.icon} text-sm`}></i>
                {cat.name}
              </Link>
            ))}
          </div>

          {filteredComics.length > 0 ? (
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4 md:gap-5 pb-16">
              {filteredComics.map((comic) => (
                <ComicCard key={comic.id} comic={comic} />
              ))}
            </div>
          ) : (
            <div className="flex flex-col items-center justify-center py-20">
              <div className="w-20 h-20 flex items-center justify-center rounded-full bg-background-100 mb-4">
                <i className="ri-emotion-sad-line text-3xl text-foreground-400"></i>
              </div>
              <p className="text-foreground-500 mb-4">没有找到相关漫画</p>
              <Link
                to="/category/all"
                className="px-4 py-2 bg-primary-500 text-background-50 rounded-full text-sm font-medium hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
              >
                查看全部漫画
              </Link>
            </div>
          )}
        </div>
      </main>
      <Footer />
      <ScrollToTop />
    </>
  );
}